import { useState, useEffect, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { PRESET_THEMES, TOKEN_GROUPS, getThemeById } from "@/lib/presets";
import { ColorSwatch, ColorPicker } from "@/components/ColorPicker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Copy, Trash2, Download, Upload, RotateCcw, Check, Palette, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

function blankFromActive(active) {
  return { name: "My Custom Theme", type: active.type, variables: { ...active.variables } };
}

export function ThemesPage() {
  const { presetThemes, customThemes, settings, updateSettings, applyTheme, setPreview, themesApi } = useApp();
  const activeId = settings?.active_theme_id;
  const [draft, setDraft] = useState(null);
  const [selectedId, setSelectedId] = useState(activeId);
  const fileRef = useRef(null);

  const all = [...presetThemes, ...customThemes];

  // load draft from selected theme
  useEffect(() => {
    const t = getThemeById(selectedId, customThemes);
    setDraft({ id: customThemes.find((c) => c.id === t.id) ? t.id : null, name: t.name, type: t.type, variables: { ...t.variables }, _sourceId: t.id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // live preview while editing
  useEffect(() => {
    if (draft) setPreview(draft.variables, draft.type);
    return () => {};
  }, [draft, setPreview]);

  useEffect(() => () => setPreview(null), [setPreview]);

  if (!draft) return null;
  const isCustom = !!draft.id;

  const setVar = (k, v) => setDraft((d) => ({ ...d, variables: { ...d.variables, [k]: v } }));

  const applyTheme_ = async () => {
    // apply currently selected (unedited) preset/custom
    applyTheme(draft.variables, draft.type);
    if (isCustom) await updateSettings({ active_theme_id: draft.id });
    else await updateSettings({ active_theme_id: draft._sourceId });
    toast.success("Theme applied");
  };

  const saveCustom = async () => {
    const payload = { name: draft.name, type: draft.type, variables: draft.variables };
    if (isCustom) {
      const updated = await themesApi.update(draft.id, payload);
      toast.success("Theme saved");
      setSelectedId(updated.id);
    } else {
      const created = await themesApi.add(payload);
      await updateSettings({ active_theme_id: created.id });
      toast.success("Custom theme created & applied");
      setSelectedId(created.id);
    }
  };

  const duplicate = async () => {
    const created = await themesApi.add({ name: `${draft.name} Copy`, type: draft.type, variables: draft.variables });
    toast.success("Theme duplicated");
    setSelectedId(created.id);
  };

  const del = async () => {
    if (!isCustom) return;
    await themesApi.remove(draft.id);
    if (activeId === draft.id) { applyTheme(PRESET_THEMES[0].variables, PRESET_THEMES[0].type); await updateSettings({ active_theme_id: PRESET_THEMES[0].id }); }
    toast.success("Theme deleted");
    setSelectedId(PRESET_THEMES[0].id);
  };

  const exportTheme = () => {
    const blob = new Blob([JSON.stringify({ name: draft.name, type: draft.type, variables: draft.variables }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${draft.name.replace(/\s+/g, "-").toLowerCase()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const importTheme = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(reader.result);
        const created = await themesApi.add({ name: parsed.name || "Imported Theme", type: parsed.type || "dark", variables: parsed.variables });
        toast.success("Theme imported");
        setSelectedId(created.id);
      } catch { toast.error("Invalid theme file"); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const reset = () => { const t = getThemeById(draft._sourceId, customThemes); setDraft((d) => ({ ...d, variables: { ...t.variables } })); toast("Reset to original"); };

  const v = draft.variables;

  return (
    <div className="h-full flex flex-col lg:flex-row overflow-hidden">
      {/* Theme list */}
      <div className="lg:w-72 shrink-0 lg:border-r overflow-y-auto p-4" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Palette size={18} style={{ color: "var(--accent-primary)" }} />
          <h2 className="font-display text-lg font-bold" style={{ color: "var(--text-primary)" }}>Theme Studio</h2>
        </div>
        <button onClick={() => setDraft(blankFromActive(getThemeById(selectedId, customThemes)))} className="w-full mb-3 h-9 rounded-xl flex items-center justify-center gap-2 text-sm font-medium" style={{ background: "var(--accent-primary)", color: "#fff" }} data-testid="theme-create-new"><Plus size={16} /> Create New</button>

        <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Presets</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {presetThemes.map((t) => <ThemeCard key={t.id} t={t} active={activeId === t.id} selected={selectedId === t.id} onClick={() => setSelectedId(t.id)} />)}
        </div>
        {customThemes.length > 0 && <>
          <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>My Themes</p>
          <div className="grid grid-cols-2 gap-2">
            {customThemes.map((t) => <ThemeCard key={t.id} t={t} active={activeId === t.id} selected={selectedId === t.id} onClick={() => setSelectedId(t.id)} />)}
          </div>
        </>}
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="max-w-xs h-10 font-display font-bold text-lg rounded-xl" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} data-testid="theme-name-input" />
          <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="h-10 rounded-xl px-3 text-sm" style={{ background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
            <option value="dark">Dark</option><option value="light">Light</option>
          </select>
          <div className="ml-auto flex flex-wrap gap-2">
            <IconBtn onClick={duplicate} icon={Copy} title="Duplicate" testId="theme-duplicate" />
            <IconBtn onClick={exportTheme} icon={Download} title="Export" testId="theme-export-button" />
            <IconBtn onClick={() => fileRef.current.click()} icon={Upload} title="Import" testId="theme-import" />
            <IconBtn onClick={reset} icon={RotateCcw} title="Reset" testId="theme-reset" />
            {isCustom && <IconBtn onClick={del} icon={Trash2} title="Delete" danger testId="theme-delete" />}
            <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={importTheme} />
            <Button onClick={saveCustom} className="h-9 text-white font-semibold" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)" }} data-testid="theme-save"><Check size={15} /> Save</Button>
            <Button onClick={applyTheme_} className="h-9 text-white font-semibold px-5" style={{ background: "var(--accent-primary)" }} data-testid="theme-apply">Apply Theme</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="space-y-5">
            {TOKEN_GROUPS.map((group) => (
              <div key={group.label}>
                <h3 className="text-xs font-semibold uppercase tracking-widest mb-2.5" style={{ color: "var(--text-muted)" }}>{group.label}</h3>
                <div className="space-y-2">
                  {group.tokens.map((tok) => (
                    <div key={tok.key} className="flex items-center gap-3">
                      <span className="text-sm w-36 shrink-0" style={{ color: "var(--text-secondary)" }}>{tok.label}</span>
                      <div className="flex-1"><ColorSwatch color={v[tok.key]} onChange={(val) => setVar(tok.key, val)} testId={`theme-token-${tok.key.replace(/--/, "")}`} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-2.5" style={{ color: "var(--text-muted)" }}>Accent Gradient</h3>
              <div className="p-3 rounded-2xl space-y-3" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }}>
                <div className="h-8 rounded-lg" style={{ background: v["--accent-gradient"] }} />
                <GradientEditor value={v["--accent-gradient"]} onChange={(g) => setVar("--accent-gradient", g)} />
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="xl:sticky xl:top-0 self-start w-full">
            <h3 className="text-xs font-semibold uppercase tracking-widest mb-2.5" style={{ color: "var(--text-muted)" }}>Live Preview</h3>
            <div className="rounded-3xl overflow-hidden flex" style={{ background: v["--bg-app"], border: `1px solid ${v["--border-subtle"]}`, minHeight: 340 }} data-testid="theme-live-preview">
              <div className="w-24 p-3 space-y-2" style={{ background: v["--bg-sidebar"] }}>
                <div className="w-8 h-8 rounded-xl" style={{ background: v["--accent-gradient"] }} />
                {["Calendar", "Today", "Tasks"].map((x, i) => (
                  <div key={x} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg" style={{ background: i === 0 ? `color-mix(in srgb, ${v["--accent-primary"]} 18%, transparent)` : "transparent" }}>
                    <div className="w-2 h-2 rounded-full" style={{ background: i === 0 ? v["--accent-primary"] : v["--text-muted"] }} />
                    <span className="text-[9px]" style={{ color: i === 0 ? v["--text-primary"] : v["--text-secondary"] }}>{x}</span>
                  </div>
                ))}
              </div>
              <div className="flex-1 p-4 relative" style={{ background: v["--bg-timeline-track"] }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold" style={{ color: v["--text-primary"] }}>Monday</span>
                  <span className="text-[10px] px-2.5 py-1 rounded-lg text-white font-semibold" style={{ background: v["--accent-primary"] }}>+ New</span>
                </div>
                <div className="absolute left-8 top-16 bottom-4 w-0.5" style={{ background: v["--timeline-spine"] }} />
                {[{ t: "Deep Work", c: v["--accent-primary"], top: 4 }, { t: "Lunch", c: "#10B981", top: 70 }, { t: "Gym", c: "#EC4899", top: 130 }].map((e, i) => (
                  <div key={i} className="absolute rounded-lg px-2 py-1.5" style={{ left: 44, right: 12, top: e.top + 44, background: `color-mix(in srgb, ${e.c} 18%, ${v["--bg-card"]})`, borderLeft: `3px solid ${e.c}` }}>
                    <span className="text-[10px] font-semibold" style={{ color: v["--text-primary"] }}>{e.t}</span>
                  </div>
                ))}
                <div className="absolute left-6 right-3 flex items-center gap-1" style={{ top: 100 }}>
                  <div className="w-2 h-2 rounded-full" style={{ background: v["--timeline-indicator"] }} />
                  <div className="flex-1 h-px" style={{ background: v["--timeline-indicator"] }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemeCard({ t, active, selected, onClick }) {
  return (
    <button onClick={onClick} className="relative p-2.5 rounded-xl text-left transition-transform hover:scale-[1.03]"
      style={{ background: t.variables["--bg-card"], border: `1.5px solid ${selected ? "var(--accent-primary)" : t.variables["--border-subtle"]}` }} data-testid="theme-preset-card">
      <div className="flex gap-1 mb-2">
        {["--accent-primary", "--timeline-indicator", "--bg-app", "--text-primary"].map((k) => <span key={k} className="w-3.5 h-3.5 rounded-full" style={{ background: t.variables[k], border: "1px solid rgba(128,128,128,0.2)" }} />)}
      </div>
      <span className="text-[11px] font-medium truncate block" style={{ color: t.variables["--text-primary"] }}>{t.name}</span>
      {active && <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "var(--accent-primary)" }}><Check size={10} className="text-white" /></span>}
    </button>
  );
}

function IconBtn({ onClick, icon: Ico, title, danger, testId }) {
  return (
    <button onClick={onClick} title={title} className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-105"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", color: danger ? "#f87171" : "var(--text-secondary)" }} data-testid={testId}>
      <Ico size={16} />
    </button>
  );
}

function GradientEditor({ value, onChange }) {
  const m = value?.match(/#[0-9a-fA-F]{6}/g) || ["#3B82F6", "#8B5CF6"];
  const [c1, setC1] = useState(m[0] || "#3B82F6");
  const [c2, setC2] = useState(m[1] || m[0] || "#8B5CF6");
  const build = (a, b) => onChange(`linear-gradient(135deg, ${a} 0%, ${b} 100%)`);
  return (
    <div className="grid grid-cols-2 gap-2">
      <ColorSwatch color={c1} onChange={(v) => { setC1(v); build(v, c2); }} label="From" />
      <ColorSwatch color={c2} onChange={(v) => { setC2(v); build(c1, v); }} label="To" />
    </div>
  );
}
